import { CodeHighlightNode, CodeNode } from '@lexical/code'
import { $generateNodesFromDOM } from '@lexical/html'
import { AutoLinkNode, LinkNode } from '@lexical/link'
import { ListItemNode, ListNode } from '@lexical/list'
import { TRANSFORMERS } from '@lexical/markdown'
import { AutoFocusPlugin } from '@lexical/react/LexicalAutoFocusPlugin'
import { CheckListPlugin } from '@lexical/react/LexicalCheckListPlugin'
import { LexicalComposer } from '@lexical/react/LexicalComposer'
import { ContentEditable } from '@lexical/react/LexicalContentEditable'
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary'
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin'
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin'
import { ListPlugin } from '@lexical/react/LexicalListPlugin'
import { MarkdownShortcutPlugin } from '@lexical/react/LexicalMarkdownShortcutPlugin'
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin'
import { TabIndentationPlugin } from '@lexical/react/LexicalTabIndentationPlugin'
import { HeadingNode, QuoteNode } from '@lexical/rich-text'
import { TableCellNode, TableNode, TableRowNode } from '@lexical/table'
import { $getRoot, LexicalEditor } from 'lexical'
import { useRef } from 'react'
import { useTask } from '~/contexts/TaskContext'
import { useConversationUuid, useTaskDoc } from '~/hooks'
import { DocReviewPopover } from './DocReviewPopover'
import './Lexical.scss'
import { ImageNode } from './nodes'
import AutoLinkPlugin from './plugins/AutoLinkPlugin'
import CodeHighlightPlugin from './plugins/CodeHighlightPlugin'
import { ContentPastePlugin } from './plugins/ContentPastePlugin'
import ListMaxIndentLevelPlugin from './plugins/ListMaxIndentLevelPlugin'
import TopbarPlugin from './plugins/TopbarPlugin'
import { md, UpdateContentPlugin } from './plugins/UpdateContentPlugin'
import { SyntaxHighlightCodeNode } from './SyntaxHighlightCodeNode'

const theme = {
  ltr: 'ltr',
  rtl: 'rtl',
  placeholder: 'editor-placeholder',
  paragraph: 'editor-paragraph',
  quote: 'editor-quote',
  heading: {
    h1: 'editor-heading-h1',
    h2: 'editor-heading-h2',
    h3: 'editor-heading-h3',
    h4: 'editor-heading-h4',
    h5: 'editor-heading-h5'
  },
  list: {
    nested: {
      listitem: 'editor-nested-listitem'
    },
    ol: 'editor-list-ol',
    ul: 'editor-list-ul',
    listitem: 'editor-listitem'
  },
  image: 'editor-image',
  link: 'editor-link',
  text: {
    bold: 'editor-text-bold',
    italic: 'editor-text-italic',
    overflowed: 'editor-text-overflowed',
    hashtag: 'editor-text-hashtag',
    underline: 'editor-text-underline',
    strikethrough: 'editor-text-strikethrough',
    underlineStrikethrough: 'editor-text-underlineStrikethrough',
    code: 'editor-text-code'
  },
  code: 'editor-code',
  codeHighlight: {
    atrule: 'editor-tokenAttr',
    attr: 'editor-tokenAttr',
    boolean: 'editor-tokenProperty',
    builtin: 'editor-tokenSelector',
    cdata: 'editor-tokenComment',
    char: 'editor-tokenSelector',
    class: 'editor-tokenFunction',
    'class-name': 'editor-tokenFunction',
    comment: 'editor-tokenComment',
    constant: 'editor-tokenProperty',
    deleted: 'editor-tokenProperty',
    doctype: 'editor-tokenComment',
    entity: 'editor-tokenOperator',
    function: 'editor-tokenFunction',
    important: 'editor-tokenVariable',
    inserted: 'editor-tokenSelector',
    keyword: 'editor-tokenAttr',
    namespace: 'editor-tokenVariable',
    number: 'editor-tokenProperty',
    operator: 'editor-tokenOperator',
    prolog: 'editor-tokenComment',
    property: 'editor-tokenProperty',
    punctuation: 'editor-tokenPunctuation',
    regex: 'editor-tokenVariable',
    selector: 'editor-tokenSelector',
    string: 'editor-tokenSelector',
    symbol: 'editor-tokenProperty',
    tag: 'editor-tokenProperty',
    url: 'editor-tokenOperator',
    variable: 'editor-tokenVariable'
  }
}

function Placeholder() {
  return <div className='editor-placeholder select-none'>Loading...</div>
}

interface LexicalEditorComponenetProps {
  editorRef?: React.RefObject<HTMLDivElement>
  index: number
  _uuid?: string
}

export function LexicalEditorComponenet({ editorRef, index, _uuid }: LexicalEditorComponenetProps) {
  const uuid = useConversationUuid(_uuid)
  const rawMarkdown = useTaskDoc(index)
  const { task: taskObj } = useTask()
  const isLastBlock = index == (taskObj?.details.orderedStateResponses?.length ?? 0) - 1
  const editorContainerRef = useRef<HTMLDivElement | null>(null)

  const editorConfig = {
    namespace: 'CurieEditor',
    theme,
    onError(error: Error) {
      throw error
    },
    editorState: (editor: LexicalEditor) => {
      const html = md.render(rawMarkdown)
      const parser = new DOMParser()
      const dom = parser.parseFromString(html, 'text/html')
      const nodes = $generateNodesFromDOM(editor, dom)
      editor.update(() => {
        const root = $getRoot()
        root.clear()
        root.append(...nodes)
      })
    },
    nodes: [
      HeadingNode,
      ListNode,
      ListItemNode,
      QuoteNode,
      SyntaxHighlightCodeNode,
      CodeNode,
      CodeHighlightNode,
      {
        replace: CodeNode,
        with: () => {
          const node = new SyntaxHighlightCodeNode(rawMarkdown)
          node.setCreateNewLine(isLastBlock)
          return node
        }
      },
      TableNode,
      TableCellNode,
      TableRowNode,
      AutoLinkNode,
      LinkNode,
      ImageNode
    ],
    editable: false
  }

  return (
    <LexicalComposer
      initialConfig={editorConfig}
      key={isLastBlock ? 'last-block' : 'not-last-block'}
    >
      <div className='editor-container bg-white relative'>
        <div className='editor-inner' ref={editorContainerRef}>
          <div ref={editorRef} className='overflow-y-auto'>
            <RichTextPlugin
              contentEditable={<ContentEditable className='editor-input' />}
              placeholder={<Placeholder />}
              ErrorBoundary={LexicalErrorBoundary}
            />
          </div>
          <HistoryPlugin />
          <AutoFocusPlugin />
          <CodeHighlightPlugin />
          <ListPlugin />
          <CheckListPlugin />
          <LinkPlugin />
          <AutoLinkPlugin />
          <TabIndentationPlugin />
          <ContentPastePlugin />
          <ListMaxIndentLevelPlugin maxDepth={7} />
          <TopbarPlugin rawMarkdown={rawMarkdown} />
          <UpdateContentPlugin markdown={rawMarkdown} containerRef={editorContainerRef} />
          <MarkdownShortcutPlugin transformers={TRANSFORMERS} />
        </div>
        <DocReviewPopover key={uuid} />
      </div>
    </LexicalComposer>
  )
}
